<template>
  <div>
    <div class="card mb-3">
      <h5 class="card-header">Report Details Export</h5>
      <div class="card-body">
        <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent="submit">
          <div class="form-row">
            <div class="form-group col-12 col-md-6">
              <label>Interview Type</label>
              <div class="interview-type-picker border rounded p-3" :class="{ 'is-invalid': formWasValidated && form.testIds.length === 0 }">
                <div v-for="type in interviewTypes" :key="type.value">
                  <div class="form-check">
                    <input
                      :id="`type-${type.value}`"
                      v-model="form.category"
                      class="form-check-input"
                      type="radio"
                      :value="type.value"
                      :disabled="isSubmitting"
                      @change="onCategoryChange(type.value)"
                    />
                    <label :for="`type-${type.value}`" class="form-check-label">{{ type.label }}</label>
                  </div>
                  <div v-if="form.category === type.value" class="ml-4 mb-1">
                    <div v-for="test in testsByCategory[type.value]" :key="test.id" class="form-check">
                      <input
                        :id="`test-${test.id}`"
                        v-model="form.testIds"
                        class="form-check-input"
                        type="checkbox"
                        :value="test.id"
                        :disabled="isSubmitting"
                      />
                      <label :for="`test-${test.id}`" class="form-check-label">{{ test.displayName }}</label>
                    </div>
                  </div>
                </div>
              </div>
              <div class="invalid-feedback">Select an interview type.</div>
            </div>
            <div class="form-group col-12 col-md-6">
              <label>Organization</label>
              <div
                class="organization-picker border rounded p-3"
                :class="{ 'is-invalid': formWasValidated && !form.allOrganizations && form.organizationIds.length === 0 }"
              >
                <div class="form-check">
                  <input
                    id="org-all"
                    v-model="form.allOrganizations"
                    class="form-check-input"
                    type="checkbox"
                    :disabled="isSubmitting"
                    @change="onAllOrganizationsChange"
                  />
                  <label for="org-all" class="form-check-label">All Organizations</label>
                </div>
                <div v-if="!form.allOrganizations" class="organization-picker-list ml-4 mt-1">
                  <div v-for="organization in organizations" :key="organization.id" class="form-check">
                    <input
                      :id="`org-${organization.id}`"
                      v-model="form.organizationIds"
                      class="form-check-input"
                      type="checkbox"
                      :value="organization.id"
                      :disabled="isSubmitting"
                    />
                    <label :for="`org-${organization.id}`" class="form-check-label">{{ organization.displayName }}</label>
                  </div>
                </div>
              </div>
              <div class="invalid-feedback">Select at least one organization, or choose All Organizations.</div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-12">
              <label>Category</label>
              <div
                class="category-picker border rounded p-3"
                :class="{ 'is-invalid': formWasValidated && !form.allCategories && form.categoryCodes.length === 0 }"
              >
                <div class="form-check">
                  <input
                    id="cat-all"
                    v-model="form.allCategories"
                    class="form-check-input"
                    type="checkbox"
                    :disabled="isSubmitting"
                    @change="onAllCategoriesChange"
                  />
                  <label for="cat-all" class="form-check-label">All Categories</label>
                </div>
                <div v-if="!form.allCategories && categories.length" class="category-picker-list ml-4 mt-1">
                  <div v-for="cat in categories" :key="cat.code" class="form-check">
                    <input
                      :id="`cat-${cat.code}`"
                      v-model="form.categoryCodes"
                      class="form-check-input"
                      type="checkbox"
                      :value="cat.code"
                      :disabled="isSubmitting"
                    />
                    <label :for="`cat-${cat.code}`" class="form-check-label">{{ cat.displayName }}</label>
                  </div>
                </div>
                <div v-if="!form.allCategories && categories.length === 0" class="text-muted small ml-4 mt-1">
                  <span v-if="isLoadingCategories"><fa icon="circle-notch" spin /> Loading categories...</span>
                  <span v-else>Select at least one interview type to see categories.</span>
                </div>
              </div>
              <div class="invalid-feedback">Select at least one category, or choose All Categories.</div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-12 col-md-6">
              <label>Start Date</label>
              <v-date-picker v-model="form.startDate" :input-props="{ class: 'form-control', placeholder: 'Start date', readonly: true, required: true }" />
            </div>
            <div class="form-group col-12 col-md-6">
              <label>End Date</label>
              <v-date-picker v-model="form.endDate" :input-props="{ class: 'form-control', placeholder: 'End date', readonly: true, required: true }" />
            </div>
          </div>
          <button type="submit" class="btn btn-primary" :disabled="isSubmitDisabled">
            <fa v-if="isSubmitting" icon="circle-notch" spin />
            <fa v-if="!isSubmitting" icon="file-export" />
            <span class="ml-2">Submit Export</span>
          </button>
        </form>
      </div>
    </div>

    <div class="card">
      <h5 class="card-header d-flex align-items-center">
        <span>Previous Exports</span>
        <button type="button" class="btn btn-sm btn-link ml-auto" :disabled="isLoading" @click="loadJobs">
          <fa v-if="isLoading" icon="circle-notch" spin />
          <span class="ml-1">Refresh</span>
        </button>
      </h5>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-sm table-striped mb-0">
            <thead>
              <tr>
                <th>Username</th>
                <th>Date/Time</th>
                <th>Interview Type</th>
                <th>Organization</th>
                <th>Category</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Download</th>
                <th class="text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="jobs.length === 0">
                <td colspan="10" class="text-center text-muted py-4">No exports found.</td>
              </tr>
              <tr v-for="job in jobs" :key="job.id">
                <td>{{ job.requestedByName }}</td>
                <td>{{ formatDateTime(job.insertDate) }}</td>
                <td>
                  <span v-if="(job.testNames || []).length === 1">{{ job.testNames[0] }}</span>
                  <span v-else-if="(job.testNames || []).length > 1" :title="job.testNames.join(', ')">Multiple ({{ job.testNames.length }})</span>
                  <span v-else>---</span>
                </td>
                <td>
                  <span v-if="!job.organizationNames || job.organizationNames.length === 0">All Organizations</span>
                  <span v-else-if="job.organizationNames.length === 1">{{ job.organizationNames[0] }}</span>
                  <span v-else :title="job.organizationNames.join(', ')">Multiple ({{ job.organizationNames.length }})</span>
                </td>
                <td>
                  <span v-if="!job.categoryNames || job.categoryNames.length === 0">All Categories</span>
                  <span v-else-if="job.categoryNames.length === 1">{{ job.categoryNames[0] }}</span>
                  <span v-else :title="job.categoryNames.join(', ')">Multiple ({{ job.categoryNames.length }})</span>
                </td>
                <td>{{ formatDate(job.startDate) }}</td>
                <td>{{ formatDate(job.endDate) }}</td>
                <td>
                  <span class="badge" :class="statusClass(job.status)">{{ job.status }}</span>
                  <div v-if="job.status === 'failed' && job.errorMessage" class="small text-muted export-error">{{ job.errorMessage }}</div>
                </td>
                <td>
                  <div v-if="job.files && job.files.length" class="export-files">
                    <button
                      v-for="pill in filePills(job.files)"
                      :key="pill.file.id"
                      type="button"
                      class="badge badge-pill badge-primary file-pill"
                      :title="pill.file.fileName"
                      @click="download(job, pill.file)"
                    >
                      <fa icon="download" />
                      <span class="ml-1">{{ pill.label }}</span>
                    </button>
                  </div>
                  <span v-else class="text-muted">---</span>
                </td>
                <td class="text-right">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="deleteJob(job)"
                    :disabled="deletingJobId === job.id || job.status === 'generating'"
                  >
                    <fa v-if="deletingJobId === job.id" icon="circle-notch" spin />
                    <span>Delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer d-flex align-items-center">
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page <= 1 || isLoading" @click="goToPage(page - 1)">Previous</button>
        <span class="mx-3 text-muted">Page {{ page }} of {{ totalPagesDisplay }} · {{ totalCount }} total</span>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPagesDisplay || isLoading" @click="goToPage(page + 1)">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const pageSize = 25;

const CATEGORIES = [
  { value: "pre-employment", label: "Pre-Employment" },
  { value: "investigation", label: "Investigation" },
  { value: "periodic", label: "Periodic" },
];

export default {
  data() {
    return {
      tests: [],
      organizations: [],
      categories: [],
      jobs: [],
      page: 1,
      totalCount: 0,
      totalPages: 0,
      formWasValidated: false,
      isSubmitting: false,
      isLoading: false,
      isLoadingCategories: false,
      deletingJobId: null,
      pollId: null,
      interviewTypes: CATEGORIES,
      form: {
        category: null,
        testIds: [],
        allOrganizations: true,
        organizationIds: [],
        allCategories: true,
        categoryCodes: [],
        startDate: null,
        endDate: null,
      },
    };
  },
  computed: {
    testsByCategory() {
      const map = {};
      for (const test of this.tests) {
        if (!map[test.category]) map[test.category] = [];
        map[test.category].push(test);
      }
      return map;
    },
    isSubmitDisabled() {
      if (this.isSubmitting || this.form.testIds.length === 0 || !this.form.startDate || !this.form.endDate) return true;
      if (!this.form.allOrganizations && this.form.organizationIds.length === 0) return true;
      if (!this.form.allCategories && this.form.categoryCodes.length === 0) return true;
      return false;
    },
    totalPagesDisplay() {
      return this.totalPages || 1;
    },
  },
  watch: {
    "form.testIds"() {
      this.form.allCategories = true;
      this.form.categoryCodes = [];
      this.loadCategories();
    },
  },
  created() {
    this.loadOptions();
    this.loadJobs();
    this.pollId = window.setInterval(() => this.loadJobs(), 30000);
  },
  beforeDestroy() {
    if (this.pollId) window.clearInterval(this.pollId);
  },
  methods: {
    onCategoryChange(categoryValue) {
      const tests = this.testsByCategory[categoryValue] || [];
      this.form.testIds = tests.length === 1 ? [tests[0].id] : [];
    },
    onAllOrganizationsChange() {
      if (this.form.allOrganizations) this.form.organizationIds = [];
    },
    onAllCategoriesChange() {
      if (this.form.allCategories) this.form.categoryCodes = [];
    },
    loadOptions() {
      this.$axios.get("/v1/admin/tests").then(res => (this.tests = res.data));
      this.$axios.get("/v1/organizations?take=1000").then(res => (this.organizations = res.data.items || []));
    },
    loadCategories() {
      if (!this.form.testIds || this.form.testIds.length === 0) {
        this.categories = [];
        return;
      }
      const params = new URLSearchParams();
      for (const id of this.form.testIds) params.append("testIds", id);
      this.isLoadingCategories = true;
      this.$axios
        .get(`/v1/admin/report-export-categories?${params.toString()}`)
        .then(res => {
          this.categories = res.data || [];
        })
        .catch(() => {
          this.categories = [];
        })
        .finally(() => {
          this.isLoadingCategories = false;
        });
    },
    loadJobs() {
      this.isLoading = true;
      return this.$axios
        .get(`/v1/admin/report-exports?page=${this.page}&pageSize=${pageSize}`)
        .then(res => {
          this.jobs = res.data.items || [];
          this.page = res.data.page || this.page;
          this.totalCount = res.data.totalCount || 0;
          this.totalPages = res.data.totalPages || 0;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    submit() {
      this.formWasValidated = true;
      if (this.isSubmitDisabled) return;
      if (this.form.startDate > this.form.endDate) {
        alert("Start date must be before or equal to end date.");
        return;
      }

      this.isSubmitting = true;
      this.$axios
        .post("/v1/admin/report-exports", {
          testIds: this.form.testIds,
          organizationIds: this.form.allOrganizations ? [] : this.form.organizationIds,
          categoryCodes: this.form.allCategories ? [] : this.form.categoryCodes,
          startDate: moment(this.form.startDate).format("YYYY-MM-DD"),
          endDate: moment(this.form.endDate).format("YYYY-MM-DD"),
        })
        .then(() => {
          this.resetForm();
          this.page = 1;
          return this.loadJobs();
        })
        .catch(error => {
          alert(`Export submit failed: ${error.response?.data?.detail || error.message}`);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    resetForm() {
      this.formWasValidated = false;
      this.form.category = null;
      this.form.testIds = [];
      this.form.allOrganizations = true;
      this.form.organizationIds = [];
      this.form.allCategories = true;
      this.form.categoryCodes = [];
      this.form.startDate = null;
      this.form.endDate = null;
      this.categories = [];
    },
    goToPage(page) {
      this.page = page;
      this.loadJobs();
    },
    filePills(files) {
      const excel = [];
      const csv = [];
      const other = [];
      for (const file of files) {
        if (file.fileFormat === "xlsx") excel.push(file);
        else if (file.fileFormat === "csv") csv.push(file);
        else other.push(file);
      }
      const label = (kind, idx, total) => (total > 1 ? `${kind} ${String(idx + 1).padStart(2, "0")}` : kind);
      return [
        ...excel.map((f, i) => ({ file: f, label: label("Excel", i, excel.length) })),
        ...csv.map((f, i) => ({ file: f, label: label("CSV", i, csv.length) })),
        ...other.map((f, i) => ({ file: f, label: label(f.fileFormat || "File", i, other.length) })),
      ];
    },
    download(job, file) {
      this.$axios.get(`/v1/admin/report-exports/${job.id}/files/${file.id}/download-url`).then(res => {
        const link = document.createElement("a");
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.href = res.data.url;
        link.click();
        link.remove();
      });
    },
    deleteJob(job) {
      if (!window.confirm("Delete this export and its files?")) return;
      this.deletingJobId = job.id;
      this.$axios
        .delete(`/v1/admin/report-exports/${job.id}`)
        .then(() => this.loadJobs())
        .finally(() => {
          this.deletingJobId = null;
        });
    },
    statusClass(status) {
      return {
        "badge-secondary": status === "pending",
        "badge-info": status === "generating",
        "badge-success": status === "done",
        "badge-danger": status === "failed",
      };
    },
    formatDate(value) {
      return value ? moment(value).format("YYYY-MM-DD") : "---";
    },
    formatDateTime(value) {
      return value ? moment(value).format("YYYY-MM-DD HH:mm") : "---";
    },
  },
};
</script>

<style lang="scss" scoped>
.export-files {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 240px;
}

.file-pill {
  cursor: pointer;
  border: 0;
  font-weight: 500;
}

.export-error {
  max-width: 260px;
  white-space: normal;
}

.interview-type-picker {
  background-color: #fff;
  min-height: 80px;

  &.is-invalid {
    border-color: #dc3545 !important;
  }
}

.organization-picker {
  background-color: #fff;
  min-height: 80px;

  &.is-invalid {
    border-color: #dc3545 !important;
  }
}

.organization-picker-list {
  max-height: 240px;
  overflow-y: auto;
}

.category-picker {
  background-color: #fff;
  min-height: 80px;

  &.is-invalid {
    border-color: #dc3545 !important;
  }
}

.category-picker-list {
  max-height: 240px;
  overflow-y: auto;
}
</style>
